import { useState, useEffect, useMemo, forwardRef, useRef } from 'react';

import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { ko } from 'date-fns/locale';

import Alert from '@shared/components/atoms/Alert';
import Button from '@shared/components/atoms/Button';
import Modal from '@shared/components/atoms/Modal/Modal';
import RecipientModal from '@features/document-write/components/RecipientModal/RecipientModal';
import { formatDateToYMD } from '@shared/utils/formatDate';
import { useVacationRequestForm } from '@features/vacation/hooks/useVacationRequestForm';
import { addDaysExcludeWeekend } from '@features/vacation/utils/dateUtils';

import styles from './VacationRequestModal.module.scss';
import calendarIcon from '@shared/assets/images/calendar_icon.svg';
import closeIcon from '@shared/assets/images/x-close_icon.svg';

/**
 * 휴가신청 모달 컴포넌트
 */
function VacationRequestModal({ isOpen, onClose, onSuccess, existingVacations = [] }) {
  const [recipientModalOpen, setRecipientModalOpen] = useState(false);
  const fileInputRef = useRef(null);

  const today = new Date();

  // Alert 상태 관리
  const [alertState, setAlertState] = useState({
    isOpen: false,
    message: '',
    isSuccess: false, // 성공 여부 추적
  });

  function showAlert(message, isSuccess = false) {
    setAlertState({
      isOpen: true,
      message,
      isSuccess,
    });
  }

  function closeAlert() {
    const wasSuccess = alertState.isSuccess;
    setAlertState({
      isOpen: false,
      message: '',
      isSuccess: false,
    });
    // Alert 닫을 때 성공 콜백 호출 (리스트 새로고침)
    if (wasSuccess && onSuccess) {
      onSuccess();
    }
  }

  // 커스텀 훅 사용
  const {
    formData,
    setFormData,
    vacationTypes,
    ceremonyTypes,
    showVacationDivision,
    showDueDate,
    showCeremonyFields,
    isLoadingVacationTypes,
    applicantInfo,
    handleVacationTypeChange,
    handleFileChange,
    handleFileRemove,
    handleSave: handleHookSave,
    saveMutation,
    saveFileMutation,
    holidaySet,
  } = useVacationRequestForm({
    isOpen,
    onClose: () => {
      onClose();
    },
    onSuccess: () => {
      showAlert('휴가 신청이 완료되었습니다.', true);
    },
    existingVacations,
  });

  // 휴가 구분 옵션 (연차, 기타)
  const vacationDivisionOptions = [
    { type: 'A01', value: '종일', label: '종일' },
    { type: 'A02', value: '반차(오전)', label: '반차(오전)' },
    { type: 'A03', value: '반차(오후)', label: '반차(오후)' },
  ];

  // 날짜 포맷팅 (yyyy-MM-dd → Date)
  function parseDate(dateStr) {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    return isNaN(d.getTime()) ? null : d;
  }

  // 저장 핸들러 (중복 체크 및 벨리데이션 포함)
  async function handleSave() {
    try {
      await handleHookSave();
    } catch (error) {
      const errorMessage =
        error?.response?.data?.error_msg || error?.message || '알 수 없는 오류가 발생했습니다.';
      showAlert(errorMessage);
    }
  }

  // 신청일자 (현재 날짜)
  const requestDate = useMemo(() => {
    return `${formatDateToYMD(today)}   ${String(today.getHours()).padStart(2, '0')}:${String(today.getMinutes()).padStart(2, '0')}:${String(today.getSeconds()).padStart(2, '0')}`;
  }, [isOpen]);

  const CustomDateInput = forwardRef(function CustomDateInput(
    { value, onClick, placeholder, id, disabled },
    ref,
  ) {
    return (
      <div className={styles.customDateInputContainer}>
        <img src={calendarIcon} alt="" className={styles.calendarIcon} />
        <input
          ref={ref}
          id={id}
          type="text"
          value={value}
          onClick={onClick}
          onChange={onClick}
          placeholder={placeholder}
          className={styles.dateInput}
          disabled={disabled}
          readOnly
        />
      </div>
    );
  });

  // 참조자 선택 핸들러
  function handleRecipientSelect(selectedList) {
    setFormData((prev) => ({ ...prev, recipients: selectedList }));
    setRecipientModalOpen(false);
  }

  const recipientList = useMemo(() => {
    if (!formData.recipients?.length) return [];

    return formData.recipients.map((r) => ({
      enumber: r.enumber || 0,
      name: r.name || '',
      rank: r.rank || '',
      position: r.position || '',
      team: r.team || '',
    }));
  }, [formData.recipients]);
  const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB
  /* 첨부파일 선택 */
  const onFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_FILE_SIZE) {
      showAlert('첨부파일은 최대 100MB까지 등록할 수 있습니다.');
      // input 값 초기화 (같은 파일 재선택 가능하게)
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    handleFileChange(file);
  };
  // const onFileChange = (e) => {
  //   const file = e.target.files?.[0];
  //   if (file) handleFileChange(file);
  // };

  /* 첨부파일 삭제 */
  const onFileRemove = () => {
    handleFileRemove();
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // 참조 문자열
  const recipientText = useMemo(() => {
    if (!formData.recipients?.length) return '';
    return formData.recipients.map((r) => (typeof r === 'string' ? r : r.name)).join(', ');
  }, [formData.recipients]);

  //첨부파일 미리보기
  const fileObjectUrl = useMemo(() => {
    if (!formData.file) return null;
    return URL.createObjectURL(formData.file);
  }, [formData.file]);
  //메모리 누수 방지
  useEffect(() => {
    return () => {
      if (fileObjectUrl) {
        URL.revokeObjectURL(fileObjectUrl);
      }
    };
  }, [fileObjectUrl]);

  const MAX_REASON_LENGTH = 1000;

  // 반차 여부
  const isHalfDay =
    showVacationDivision &&
    (formData.vacationDivision === 'A02' || formData.vacationDivision === 'A03');

  function handleReasonPaste(e) {
    const pasteData = e.clipboardData?.getData('text') || '';
    const currentValue = formData.reason || '';

    const target = e.target;
    const selectionStart = target.selectionStart ?? 0;
    const selectionEnd = target.selectionEnd ?? 0;

    const beforeSelection = currentValue.substring(0, selectionStart);
    const afterSelection = currentValue.substring(selectionEnd);

    const expectedValue = beforeSelection + pasteData + afterSelection;

    if (expectedValue.length > MAX_REASON_LENGTH) {
      e.preventDefault(); // 붙여넣기 자체 차단
      showAlert(`신청 사유는 최대 ${MAX_REASON_LENGTH}자까지 입력할 수 있습니다.`);
    }
  }

  const isCeremony = showCeremonyFields && formData.ceremonyType;
  const isSpouseBirth = isCeremony && formData.ceremonyType === 'Z01';

  // 주말 허용 여부
  const allowWeekend = isCeremony && !isSpouseBirth;

  // 주말 공휴일 제외
  const isGeneralVacation = !showCeremonyFields || formData.ceremonyType === 'Z01';
  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title="휴가 신청"
        size="medium"
        closeOnOverlayClick={false}
      >
        <table className={styles.modalContent}>
          <tbody>
            {/* 신청자 */}
            <tr>
              <td>기안자</td>
              <td>{applicantInfo}</td>
            </tr>
            {/* 신청 일자 */}
            <tr>
              <td>신청 일자</td>
              <td>{requestDate}</td>
            </tr>
            {/* 휴가 종류 */}
            <tr>
              <td>휴가 종류</td>
              <td>
                <select
                  className={styles.select}
                  value={formData.vacationType}
                  onChange={(e) => handleVacationTypeChange(e.target.value)}
                  disabled={isLoadingVacationTypes}
                >
                  {vacationTypes.map((type) => (
                    <option key={type.code} value={type.code.toString()}>
                      {type.name}
                    </option>
                  ))}
                </select>
              </td>
            </tr>
            {/* 휴가 구분 (연차) */}
            {showVacationDivision && (
              <tr>
                <td>휴가 구분</td>
                <td>
                  <select
                    className={styles.select}
                    value={formData.vacationDivision}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, vacationDivision: e.target.value }))
                    }
                  >
                    {vacationDivisionOptions.map((option) => (
                      <option key={option.type} value={option.type}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </td>
              </tr>
            )}
            {/* 출산 예정일 (출산전후휴가일 경우만) */}
            {showDueDate && (
              <tr>
                <td>출산 예정일</td>
                <td>
                  <DatePicker
                    selected={formData.dueDate || today}
                    onChange={(date) => {
                      const formatted = formatDateToYMD(date);
                      setFormData((prev) => ({ ...prev, dueDate: formatted }));
                    }}
                    locale={ko}
                    dateFormat="yyyy-MM-dd"
                    placeholderText="날짜 선택"
                    customInput={<CustomDateInput />}
                  />
                </td>
              </tr>
            )}
            {/* 경조 구분 (경조 휴가일 경우만) */}
            {showCeremonyFields && (
              <tr>
                <td>경조 구분</td>
                <td>
                  <select
                    className={styles.select}
                    value={formData.ceremonyType}
                    onChange={(e) => {
                      setFormData((prev) => ({
                        ...prev,
                        ceremonyType: e.target.value,
                        ceremonyTarget: '', // 경조 구분 변경 시 대상 초기화
                        startDate: today, // 경조 구분 변경 시 시작일 초기화
                        endDate: today, // 경조 구분 변경 시 종료일 초기화
                        period: 1, // 경조 구분 변경 시 휴가기간 초기화
                      }));
                    }}
                  >
                    <option value="">선택</option>
                    {ceremonyTypes.map((type) => (
                      <option key={type.type} value={type.type}>
                        {type.name}
                      </option>
                    ))}
                  </select>
                </td>
              </tr>
            )}
            {/* 경조 대상 (경조 휴가일 경우만) */}
            {showCeremonyFields &&
              formData.ceremonyType &&
              'A01' !== formData.ceremonyType &&
              'Z01' !== formData.ceremonyType && (
                <tr>
                  <td>경조 대상</td>
                  <td>
                    <input
                      type="text"
                      className={styles.input}
                      value={formData.ceremonyTarget}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, ceremonyTarget: e.target.value }))
                      }
                      placeholder="경조 대상을 입력해 주세요."
                    />
                  </td>
                </tr>
              )}
            {/* 휴가 일정 */}
            <tr>
              <td>휴가 일정</td>
              <td>
                <div className={styles.vacationScheduleWrapper}>
                  <div className={styles.dateInputGroup}>
                    <div className={styles.dateInputWrapper}>
                      <DatePicker
                        selected={formData.startDate || today}
                        onChange={(date) => {
                          const formatted = formatDateToYMD(date);
                          setFormData((prev) => ({
                            ...prev,
                            startDate: formatted,
                            endDate:
                              date > parseDate(formData.startDate) ? formatted : prev.endDate,
                          }));
                        }}
                        locale={ko}
                        dateFormat="yyyy-MM-dd"
                        placeholderText="시작일"
                        className={styles.dateInput}
                        customInput={<CustomDateInput />}
                        filterDate={(date) => {
                          const formatted = formatDateToYMD(date);
                          // if (allowWeekend) return true;
                          // const day = date.getDay();
                          // return day !== 0 && day !== 6; // 일요일(0), 토요일(6) 제외

                          // 경조 휴가 중 일부는 주말 허용
                          if (!allowWeekend) {
                            const day = date.getDay();
                            if (day === 0 || day === 6) return false;
                          }

                          // 일반 휴가일 경우 공휴일 차단
                          if (isGeneralVacation && holidaySet.has(formatted)) {
                            return false;
                          }
                          return true;
                        }}
                      />
                    </div>

                    <span className={styles.dateSeparator}>~</span>

                    <div className={styles.dateInputWrapper}>
                      <DatePicker
                        selected={formData.endDate || today}
                        minDate={parseDate(formData.startDate)}
                        maxDate={
                          // 배우자자녀출산 휴가인 경우
                          showCeremonyFields &&
                          formData.ceremonyType &&
                          'Z01' === formData.ceremonyType
                            ? (() => {
                                const start = parseDate(formData.startDate);
                                if (!start) return null;
                                return addDaysExcludeWeekend(start, 20, holidaySet);
                              })()
                            : null
                        }
                        onChange={(date) => {
                          const formatted = formatDateToYMD(date);
                          setFormData((prev) => ({ ...prev, endDate: formatted }));
                        }}
                        locale={ko}
                        dateFormat="yyyy-MM-dd"
                        placeholderText="종료일"
                        className={styles.dateInput}
                        disabled={
                          isHalfDay || //  연차 + 반차 = 종료일 수정 불가
                          (showCeremonyFields &&
                            formData.ceremonyType &&
                            (() => {
                              const ceremonyTypeName =
                                ceremonyTypes.find((t) => t.type === formData.ceremonyType)?.name ||
                                '';
                              // 배우자·자녀 출산만 예외
                              return !(
                                ceremonyTypeName.includes('배우자') &&
                                ceremonyTypeName.includes('자녀') &&
                                ceremonyTypeName.includes('출산')
                              );
                            })())
                        }
                        customInput={<CustomDateInput />}
                        filterDate={(date) => {
                          const formatted = formatDateToYMD(date);
                          // if (allowWeekend) return true;
                          // const day = date.getDay();
                          // return day !== 0 && day !== 6; // 일요일(0), 토요일(6) 제외

                          // 경조 휴가 중 일부는 주말 허용
                          if (!allowWeekend) {
                            const day = date.getDay();
                            if (day === 0 || day === 6) return false;
                          }

                          // 일반 휴가일 경우 공휴일 차단
                          if (isGeneralVacation && holidaySet.has(formatted)) {
                            return false;
                          }
                          return true;
                        }}
                      />
                    </div>
                    <span className={styles.periodInfo}>
                      휴가기간 :
                      <input
                        type="text"
                        className={styles.input}
                        value={formData.period}
                        onChange={(e) =>
                          setFormData((prev) => ({ ...prev, period: e.target.value }))
                        }
                        readOnly
                      />
                      일
                    </span>
                  </div>
                  {/* 배우자·자녀 출산 휴가 안내 */}
                  {showCeremonyFields &&
                    formData.ceremonyType &&
                    'Z01' === formData.ceremonyType && (
                      <div className={styles.warningText}>
                        배우자 출산휴가는 출산일로부터 120일 안에 총 20일을 모두 사용해야 하며,
                        <br />
                        최대 3번까지 나누어 총 4회로 사용할 수 있음.
                      </div>
                    )}
                </div>
              </td>
            </tr>
            {/* 신청 사유 */}
            <tr className={styles.textareaArea}>
              <td>신청 사유</td>
              <td>
                <textarea
                  className={styles.textarea}
                  value={formData.reason}
                  onPaste={handleReasonPaste}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value.length > MAX_REASON_LENGTH) {
                      return;
                    }

                    setFormData((prev) => ({ ...prev, reason: value }));
                  }}
                  placeholder="신청 사유를 입력해주세요."
                  rows={5}
                />
                {/* 글자 수 카운터 */}
                <div className={styles.charCounter}>
                  <span
                    className={
                      formData.reason?.length >= MAX_REASON_LENGTH ? styles.limitReached : ''
                    }
                  >
                    {formData.reason?.length || 0}
                  </span>
                  /{MAX_REASON_LENGTH}자
                </div>
              </td>
            </tr>
            {/* 첨부파일 */}
            <tr>
              <td>첨부파일</td>
              <td>
                <div className={styles.fileInputGroup}>
                  <input
                    type="file"
                    id="vacation-file"
                    ref={fileInputRef}
                    className={styles.inputButton}
                    onChange={onFileChange}
                  />
                  <label htmlFor="vacation-file" className={styles.inputButtonLabel}>
                    파일 선택
                  </label>
                  {formData.file && (
                    <div className={styles.fileNameArea}>
                      <a
                        href={fileObjectUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.fileLink}
                        title={formData.file.name}
                      >
                        {formData.file.name}
                      </a>

                      <button
                        type="button"
                        className={styles.fileRemoveButton}
                        onClick={onFileRemove}
                        aria-label="첨부파일 삭제"
                      >
                        <img src={closeIcon} alt="" className={styles.closeIcon} />
                      </button>
                    </div>
                  )}
                </div>
              </td>
            </tr>
            {/* 참조자 */}
            <tr>
              <td>참조자</td>
              <td>
                <div className={styles.fileInputGroup}>
                  <button
                    type="button"
                    className={styles.inputButtonLabel}
                    onClick={() => {
                      setRecipientModalOpen(true);
                    }}
                  >
                    선택
                  </button>
                  <input
                    type="text"
                    className={styles.input}
                    value={recipientText}
                    title={recipientText}
                    placeholder=""
                    readOnly
                  />
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        {/* 저장 버튼 */}
        <div className={styles.buttonGroup}>
          {/* 취소 버튼 */}
          <Button
            variant="default"
            onClick={onClose}
            className={styles.cancelButton}
            disabled={false}
          >
            취소
          </Button>
          {/* 저장 버튼 */}
          <Button
            variant="default"
            onClick={handleSave}
            className={styles.saveButton}
            disabled={saveMutation.isPending || saveFileMutation.isPending}
          >
            {saveMutation.isPending || saveFileMutation.isPending ? '저장 중...' : '저장'}
          </Button>
        </div>
      </Modal>

      {/* 참조자 선택 모달 */}
      <RecipientModal
        isOpen={recipientModalOpen}
        onClose={() => {
          setRecipientModalOpen(false);
        }}
        onSelect={handleRecipientSelect}
        title="참조자 선택"
        selectedRecipients={recipientList}
      />

      {/* Alert 컴포넌트 */}
      <Alert isOpen={alertState.isOpen} message={alertState.message} onClose={closeAlert} />
    </>
  );
}

export default VacationRequestModal;
